import { useEffect, useRef, useState } from "react";
import { GameFullscreenButton } from "../GameFullscreenButton";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CRITTERS } from "../../data/dolopals";
import { SectionHeading } from "../SectionHeading";
import { critterOfTheWeek } from "./utils";
import { critterCry } from "./audio";
import { CritterShape } from "./CritterShape";

const PAGE_SIZE = 10; // field-guide pages keep the book short — 5 pages of 10

export function CritterBook({ exp, awardShards, awardPoints, collectCritter, saveMatchBest, startInMatch = false }) {
  const owned = exp.critters || [];
  const [matchMode, setMatchMode] = useState(Boolean(startInMatch));
  const [page, setPage] = useState(0);
  const pageCount = Math.ceil(CRITTERS.length / PAGE_SIZE);
  const shown = CRITTERS.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // banner "PLAY CRITTER MATCHING GAME" flips the book into match mode
  useEffect(() => {
    const on = () => setMatchMode(true);
    window.addEventListener("dolo-play-match", on);
    return () => window.removeEventListener("dolo-play-match", on);
  }, []);
  return (
    <section className="critter-book" data-testid="critter-book">
      <SectionHeading
        eyebrow="FIELD GUIDE // SCRAP CRITTERS"
        title="The critter book."
        text="Fifty little scavengers roam the Titan Tracker grid. When one wanders in, tap it and it moves into this book. The silhouettes show exactly who's still hiding out there. Tap any adopted critter to hear the call it sang when you caught it."
      />
      <div className="critter-book-bar">
        <p className="critter-book-count" data-testid="critter-book-count">
          {owned.length}/{CRITTERS.length} ADOPTED{(exp.shinies || []).length > 0 ? ` · ★ ${(exp.shinies || []).length} SHINY` : ""}
        </p>
        <button
          type="button"
          className="critter-match-toggle"
          onClick={() => setMatchMode((v) => !v)}
          data-testid="critter-match-toggle"
        >
          {matchMode ? "BACK TO THE BOOK" : "PLAY THE MATCH GAME"}
        </button>
      </div>
      {matchMode ? (
        <CritterMatch awardShards={awardShards} awardPoints={awardPoints} collectCritter={collectCritter} saveMatchBest={saveMatchBest} />
      ) : (
      <>
      <div className="critter-book-grid">
        {shown.map((c, i) => {
          const num = page * PAGE_SIZE + i + 1;
          const has = owned.includes(c.id);
          const shiny = (exp.shinies || []).includes(c.id);
          return (
            <div
              key={c.id}
              className={`critter-tile ${has ? "is-owned" : ""} ${shiny ? "is-shiny" : ""}`}
              data-testid={`critter-tile-${c.id}`}
              style={has ? { "--cc": shiny ? "#ffd700" : c.color } : undefined}
              onClick={has ? () => critterCry(c.id) : undefined}
              role={has ? "button" : undefined}
              title={has ? `HEAR THE ${c.name} CALL` : undefined}
            >              <i className="critter-num">{String(num).padStart(2, "0")}</i>
              <span className="critter-figure">
                <CritterShape shape={c.shape} color={shiny ? "#ffd700" : has ? c.color : "#242a31"} />
              </span>
              <strong>{has ? c.name : "???"}</strong>
              <em>{shiny ? "★ SHINY" : has ? "ADOPTED" : "STILL HIDING"}</em>
              {c.id === critterOfTheWeek().id ? <i className="critter-weekly-tag">OF THE WEEK</i> : null}
            </div>
          );
        })}
      </div>
      <div className="critter-book-pager" data-testid="critter-book-pager">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
          data-testid="critter-page-prev"
        >
          <ChevronLeft size={14} /> PREV
        </button>
        <span data-testid="critter-page-count">PAGE {page + 1}/{pageCount}</span>
        <button
          type="button"
          onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          disabled={page === pageCount - 1}
          data-testid="critter-page-next"
        >
          NEXT <ChevronRight size={14} />
        </button>
      </div>
      </>
      )}
    </section>
  );
}

// CRITTER MATCH — quick pairs game living inside the critter book; every round won
// pays shards + points, every 3rd round adopts a prize critter, and the clock speeds up

export function CritterMatch({ awardShards, awardPoints, collectCritter, saveMatchBest }) {
  const [game, setGame] = useState(null); // {round, deck, flipped, left, status, time, prize}
  const [best, setBest] = useState(0);
  const [banked, setBanked] = useState(0);
  const flipBackRef = useRef(null);

  const roundTime = (r) => Math.max(12, 65 - r * 5); // 60s round one, 5s faster every win

  const startRound = (round) => {
    if (flipBackRef.current) window.clearTimeout(flipBackRef.current);
    const pairsCount = Math.min(8, 3 + round);
    const picks = [...CRITTERS].sort(() => Math.random() - 0.5).slice(0, pairsCount);
    const deck = [...picks, ...picks]
      .map((c) => ({ key: Math.random().toString(36).slice(2), id: c.id }))
      .sort(() => Math.random() - 0.5);
    setGame({ round, deck, flipped: [], left: pairsCount, status: "playing", time: roundTime(round), prize: null });
    window.__match = { deck: deck.map((d) => d.id) }; // QA handle
  };

  // round clock — one tick a second, running dry ends the run
  useEffect(() => {
    if (!game || game.status !== "playing") return undefined;
    const t = window.setInterval(() => {
      setGame((g) => {
        if (!g || g.status !== "playing") return g;
        return g.time <= 1 ? { ...g, time: 0, status: "lost" } : { ...g, time: g.time - 1 };
      });
    }, 1000);
    return () => window.clearInterval(t);
  }, [game?.round, game?.status]);

  useEffect(() => () => flipBackRef.current && window.clearTimeout(flipBackRef.current), []);

  const flip = (i) => {
    if (!game || game.status !== "playing" || game.flipped.length === 2) return;
    const card = game.deck[i];
    if (!card || card.matched || game.flipped.includes(i)) return;
    const flipped = [...game.flipped, i];
    let next = { ...game, flipped };
    if (flipped.length === 2) {
      const [a, b] = flipped;
      if (game.deck[a].id === game.deck[b].id) {
        const deck = game.deck.map((d, idx) => (idx === a || idx === b ? { ...d, matched: true } : d));
        const left = game.left - 1;
        next = { ...next, deck, flipped: [], left };
        critterCry(card.id);
        if (left === 0) {
          const pay = 10 * game.round;
          awardShards?.(pay);
          awardPoints?.("dolopals", 2);
          setBanked((v) => v + pay);
          setBest((v) => Math.max(v, game.round));
          saveMatchBest?.(game.round);
          let prize = null;
          if (game.round % 3 === 0) {
            prize = CRITTERS[Math.floor(Math.random() * CRITTERS.length)];
            collectCritter?.(prize.id, false);
          }
          next = { ...next, status: "won", prize };
        }
      } else {
        flipBackRef.current = window.setTimeout(() => {
          setGame((cur) => (cur && cur.status === "playing" ? { ...cur, flipped: [] } : cur));
        }, 750);
      }
    }
    setGame(next);
  };

  return (
    <div className="match-game" data-testid="match-game">
      {!game ? (
        <div className="match-intro">
          <p>
            Flip two cards — if the same critter sings, the pair stays lit. Clear the board before the clock dies.
            Every round you win, the next clock runs faster. Every 3rd round pays a PRIZE CRITTER straight into your
            book.
          </p>
          <button type="button" className="cry-quiz-play" onClick={() => startRound(1)} data-testid="match-start">
            START ROUND 1 — {roundTime(1)}S CLOCK
          </button>
        </div>
      ) : (
        <>
          <div className="match-hud" data-testid="match-hud">
            <span>ROUND <b data-testid="match-round">{game.round}</b></span>
            <span>PAIRS LEFT <b data-testid="match-pairs-left">{game.left}</b></span>
            <span>BEST ROUND <b data-testid="match-best">{best}</b></span>
            <span>BANKED <b data-testid="match-banked">{banked}</b> SHARDS</span>
          </div>
          <div className="match-timebar" data-testid="match-timebar">
            <span className={game.time <= 10 ? "is-low" : ""} style={{ width: `${(game.time / roundTime(game.round)) * 100}%` }} />
          </div>
          <p className="match-clock" data-testid="match-timer">{game.time}S ON THE CLOCK</p>
          <div className="match-board" data-testid="match-board">
            <GameFullscreenButton testid="critter-match-fullscreen" />
            {game.deck.map((card, i) => {
              const c = CRITTERS.find((x) => x.id === card.id);
              const up = card.matched || game.flipped.includes(i);
              return (
                <button
                  key={card.key}
                  type="button"
                  className={`match-card ${up ? "is-up" : ""} ${card.matched ? "is-matched" : ""}`}
                  style={{ "--cc": c.color, "--i": i }}
                  onClick={() => flip(i)}
                  disabled={game.status !== "playing"}
                  data-testid={`match-card-${i}`}
                  aria-label={up ? c.name : "hidden critter card"}
                >
                  {up ? <CritterShape shape={c.shape} color={c.color} size={44} /> : <span className="match-card-back">?</span>}
                </button>
              );
            })}
          </div>
          {game.status === "won" ? (
            <div className="match-outcome is-won" data-testid="match-won">
              <p>
                ROUND {game.round} CLEARED — +{10 * game.round} SCRAP-SHARDS · +2 PTS
                {game.prize ? ` · PRIZE CRITTER: ${game.prize.name} JOINED YOUR BOOK!` : ""}
              </p>
              <button type="button" className="cry-quiz-play" onClick={() => startRound(game.round + 1)} data-testid="match-next">
                NEXT ROUND — {roundTime(game.round + 1)}S CLOCK
              </button>
            </div>
          ) : null}
          {game.status === "lost" ? (
            <div className="match-outcome is-lost" data-testid="match-lost">
              <p>SIGNAL LOST — YOU REACHED ROUND {game.round} · {banked} SCRAP-SHARDS BANKED</p>
              <button type="button" className="cry-quiz-play" onClick={() => startRound(1)} data-testid="match-retry">
                RUN IT BACK — ROUND 1
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/* Trophy Wall — every radar/kidzone milestone on one shelf; locked trophies whisper the hunt */
// NAME THAT CRY — hear a species call, tap the critter that sang it, bank scrap-shards
